import { useState } from "react"
import { FaChevronDown, FaChevronUp } from "react-icons/fa6"
import { IoCloseOutline } from "react-icons/io5"
import { useNavigate } from "react-router-dom"

import { useEmailVerification } from "@fuse/hooks/useEmailVerification"
import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import Bottom from "@fuse/sharedComponents/bottom"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import Navbar from "@fuse/sharedComponents/navbar/Navbar"
import { has, isEmpty } from "lodash-es"

import {
  DownloadLinkMap,
  onOpenDownloadLink,
  useDeviceDetection,
} from "@fuse/utils/DeviceDection"

import AskDOBPopup from "@fuse/pages/profilePage/components/AskDOBPopup"
import AskEmailPopup from "@fuse/pages/profilePage/components/AskEmailPopup"
import AskGenderPopup from "@fuse/pages/profilePage/components/AskGenderPopup"
import ProfilePicturePopup from "@fuse/pages/profilePage/components/ProfilePicturePopup"
import useProfileController, {
  DetailRowProps,
  EDetailsRowOperation,
  SettingOption,
} from "@fuse/pages/profilePage/useProfileController"

import IconEdit from "@fuse/assets/images/Profile/img-icon-edit.svg"

import EmailVerificationPopup from "../eventPage/components/EmailVerificationPopup"

const bgImage = "/assets/alpha-event-not-found.svg"

const ProfilePage = () => {
  const {
    user,
    isLoading,
    settingHolder,
    onSettingPress,
    detailsRowOperation,
    detailsRowHolder,
    onDetailRawPress,
    // onVerifyEmail,
    isEmailInboxVerification,
    setIsEmailInboxVerification,
    onResendMail,
    onUpdateUserDetails,
    onCloseDetailRowPopup,
    onEditProfilePicture,
    profilePictureOptions,
    onUploadProfilePicture,
  } = useProfileController()

  const isUserLoggedIn = has(user, "uid")
  const {
    verifyEmail,
    isLoading: loadingEmailVerification,
    startPolling,
  } = useEmailVerification()

  // Email verification functions - now simplified using the hook
  const onVerifyEmail = async (email: string) => {
    const result = await verifyEmail(email, {
      onSuccess: () => {
        setIsEmailInboxVerification(true)
        // Success callback for both dev and production flows
      },
      onCloseUI: () => {
        // Dev mode: immediate verification, close UI and continue
        // setShowEmailVerificationPopup(false)
        setIsEmailInboxVerification(false)
      },
      onRequireInboxCheck: () => {
        // Production mode: show inbox check UI
        setIsEmailInboxVerification(true)

        // Start polling for verification with cleanup
        startPolling(() => {
          // setShowEmailVerificationPopup(false)
          onCloseDetailRowPopup()

          setIsEmailInboxVerification(false)
        })
      },
      onVerificationComplete: () => {
        // Handle completion callback if needed
      },
    })

    return result
  }

  const DetailRow: React.FC<DetailRowProps> = ({
    label,
    value,
    className,
    disabled,
    onClick,
  }) => {
    return (
      <div
        className={`flex justify-between text-white text-sm ${disabled ? "" : "cursor-pointer"} ${className}`}
        onClick={() => {
          if (!disabled) {
            onClick()
          }
        }}
      >
        <span>{label}</span>
        <span className="text-white/35">{value}</span>
      </div>
    )
  }

  const HeaderClose: React.FC = () => {
    const navigate = useNavigate()
    const handleBackPress = () => {
      navigate(-1)
    }
    return (
      <div className="">
        <div
          onClick={handleBackPress}
          className="absolute right-4 md:right-12 w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10  cursor-pointer z-10"
        >
          <IoCloseOutline className="text-white text-md md:text-3xl" />
        </div>
      </div>
    )
  }

  const MenuButton: React.FC<SettingOption> = ({
    icon,
    label,
    isExpandable,
    isOpen,
    accordionOptions,
    elIndex,
    isDestructive,
  }) => {
    return (
      <BlurWrapper
        overlayOpacityDefault="15"
        className="flex flex-col items-center justify-between w-full py-3 px-3 mt-2 rounded-xl bg-white/10 backdrop-blur-none"
      >
        <div
          className="flex w-full items-center cursor-pointer"
          onClick={() => onSettingPress(elIndex)}
        >
          <div className="flex flex-1 flex-row items-center gap-3">
            {isDestructive ? (
              <div className="p-2 rounded-lg bg-red-500">{icon}</div>
            ) : (
              <BlurWrapper
                overlayOpacityDefault="15"
                className="p-2 rounded-lg bg-white/10 backdrop-blur-none"
              >
                {icon}
              </BlurWrapper>
            )}

            <p
              className={`${isDestructive ? "text-red-500" : "text-white"} text-sm`}
            >
              {label}
            </p>
          </div>
          {isExpandable &&
            (isOpen ? (
              <FaChevronUp color="white" className="text-sm" />
            ) : (
              <FaChevronDown color="white" className="text-sm" />
            ))}
        </div>
        {isOpen && !isEmpty(accordionOptions) && (
          <div className="flex flex-col items-start justify-start w-full text-white/35 text-sm overflow-hidden mt-1">
            {accordionOptions?.map((item, index) => {
              return (
                <div
                  key={index.toString()}
                  className="mx-11 py-2 flex w-full cursor-pointer"
                  onClick={item?.onPress}
                >
                  {item?.label}
                </div>
              )
            })}
          </div>
        )}
      </BlurWrapper>
    )
  }

  const getDetailRowClass = (index: number) => {
    switch (index) {
      case 0:
        return "pb-2"

      case 1:
        return "border-y-[0.4px] py-2 border-white/10"

      case 2:
        return "pt-2"

      default:
        return ""
    }
  }

  const renderDetailRowOperationView = () => {
    switch (detailsRowOperation) {
      case EDetailsRowOperation.Email:
        return (
          <EmailVerificationPopup
            isLoading={loadingEmailVerification}
            setIsEmailInboxVerification={setIsEmailInboxVerification}
            onVerifyEmail={onVerifyEmail}
            isEmailInboxVerification={isEmailInboxVerification}
            onClose={() => {
              setIsEmailInboxVerification(false)
              onCloseDetailRowPopup()
            }}
          />
        )

      case EDetailsRowOperation.DOB:
        return (
          <AskDOBPopup
            isLoading={isLoading}
            user={user}
            onUpdateUserDetails={onUpdateUserDetails}
            onCloseDetailRowPopup={onCloseDetailRowPopup}
          />
        )

      case EDetailsRowOperation.Gender:
        return (
          <AskGenderPopup
            isLoading={isLoading}
            onUpdateUserDetails={onUpdateUserDetails}
            onCloseDetailRowPopup={onCloseDetailRowPopup}
          />
        )

      case EDetailsRowOperation.ProfilePicture:
        return (
          <ProfilePicturePopup
            user={user}
            onCloseDetailRowPopup={onCloseDetailRowPopup}
            onUploadProfilePicture={onUploadProfilePicture}
          />
        )

      default:
        return null
    }
  }

  return (
    <BackgroundWrapper bgImage={bgImage} className="md:min-h-[100vh]">
      <BlurWrapper
        blurKey={blurKeys?.mainBlurView}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="h-full flex flex-col overflow-auto md:!min-h-[100vh]"
      >
        <div className="pt-8" id="upper-body">
          <HeaderClose />
        </div>

        {isLoading && <LoadingSpinner />}

        {!isEmpty(detailsRowOperation) && (
          <div
            className="z-[200] fixed inset-0 flex  justify-center bg-gray-800 bg-opacity-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                onCloseDetailRowPopup()
              }
            }}
          >
            <div className="h-[100vh] w-full">
              {isLoading && <LoadingSpinner />}

              {renderDetailRowOperationView()}
            </div>
          </div>
        )}

        <div className="flex flex-grow h-full items-center justify-center relative">
          {!isUserLoggedIn && (
            <div className="absolute inset-0 z-10 pointer-events-auto"></div>
          )}
          <div
            id="body"
            className="flex items-center justify-center w-full px-4 max-w-sm md:max-w-md self-center pt-8 pb-28"
          >
            <div className="flex flex-col items-center w-full">
              {/* Profile Image */}
              <div className="relative w-24 h-24">
                <img
                  // src={"https://picsum.photos/200/200"}
                  src={user?.avatarUrl}
                  alt="profile"
                  className="rounded-full w-full h-full object-cover cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    onEditProfilePicture()
                  }}
                />
                <BlurWrapper
                  blurKey={blurKeys?.mainBlurView}
                  overlayOpacityDefault="15"
                  className="absolute bottom-1 right-1 p-1 rounded-full cursor-pointer"
                  onClick={onEditProfilePicture}
                >
                  <img src={IconEdit} alt="IconEdit" />
                </BlurWrapper>
              </div>

              {/* Profile Name */}
              <h1 className="mt-4 text-xl font-semibold text-white">
                {user?.name
                  ? user?.name
                  : `${user?.firstName} ${user?.lastName}`}
              </h1>

              {/* Profile Details */}
              <BlurWrapper
                blurKey={blurKeys?.mainBlurView}
                overlayOpacityDefault="15"
                className="mt-6 w-full rounded-xl p-4 bg-white/10 backdrop-blur-none"
              >
                {detailsRowHolder?.map((item, index) => {
                  return (
                    <DetailRow
                      key={index.toString()}
                      label={item?.label}
                      value={item?.value}
                      disabled={item?.disabled}
                      className={getDetailRowClass(index)}
                      onClick={() => onDetailRawPress(index)}
                    />
                  )
                })}
              </BlurWrapper>

              {/* Buttons */}
              <div className="mt-4 w-full">
                {settingHolder?.map((item, index) => {
                  return (
                    <MenuButton
                      key={index.toString()}
                      {...item}
                      elIndex={index}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>

        <Bottom />
      </BlurWrapper>
    </BackgroundWrapper>
  )
}

export default ProfilePage
